import type { AttributionReport } from '@/api/attribution'
import { TmtAttributedBadge, type TmtModelScope } from '@/components/TmtAttributedBadge'
import { NOT_TRACKED_LABEL } from '@/components/dataQuality'
import { TooltipBadge } from '@/components/TooltipBadge'

import {
  hasSpend,
  META_SPEND_CAVEAT,
  notTrackedExplanation,
  splitsNotTracked,
  spendSourceLabel,
  spendUnavailableReason,
  unattributedExplanation,
} from './definitions'

/** The badge on every TmT figure on this page: the model (or all four), and every website's production orders. */
export function TmtBadge({ model, lookbackDays }: { model: TmtModelScope; lookbackDays: number }) {
  return <TmtAttributedBadge model={model} lookbackDays={lookbackDays} className="attr-badge" allWebsites />
}

/** The badge on Spend, CPA and ROAS: Meta's own reported spend when there is some, otherwise plainly not live. */
export function SpendBadge({ report }: { report: AttributionReport }) {
  return (
    <TooltipBadge
      tone={report.spendSource === 'meta' ? 'info' : 'neutral'}
      label={spendSourceLabel(report)}
      tooltip={hasSpend(report) ? META_SPEND_CAVEAT : spendUnavailableReason(report)}
      className="attr-badge"
    />
  )
}

/** The explanation of the unattributed row, wherever its figure appears. */
export function UnattributedBadge({ report }: { report: AttributionReport }) {
  return (
    <TooltipBadge
      tone="warning"
      label="Unattributed"
      tooltip={unattributedExplanation(report.groupBy, report.lookbackDays)}
      className="attr-badge"
    />
  )
}

/** The explanation of the Not tracked row and share: orders with no tracked visit, never merged with direct. */
export function NotTrackedBadge({ lookbackDays }: { lookbackDays: number }) {
  return <TooltipBadge tone="neutral" label={NOT_TRACKED_LABEL} tooltip={notTrackedExplanation(lookbackDays)} className="attr-badge" />
}

/** The badge beside a stated unattributed share: Not tracked once the report splits it out, otherwise Unattributed. */
export function ShareBadge({ report }: { report: AttributionReport }) {
  return splitsNotTracked(report) ? <NotTrackedBadge lookbackDays={report.lookbackDays} /> : <UnattributedBadge report={report} />
}
